// Brand panel for the auth page — a 1:1 port of the marketing site's
// (www.xpherium.com/intelligence-navigator) left-hand "Meta-style" panel so
// signing in at app.xpherium.com (web AND the desktop app, which renders the
// same origin) looks identical to signing in from the marketing site:
// soft blue gradient, product pitch + checklist, and the animated
// conversational-analytics demo inside a laptop mockup.
import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check } from 'lucide-react';
import XpheriumLogo from './XpheriumLogo';

/* ── Chart mocks (ported from landing premium/Mocks.tsx) ── */

function ScatterChart({ title }) {
  const pts = [
    [6, 78], [10, 60], [12, 92], [16, 40], [20, 70], [22, 30], [26, 88], [30, 52],
    [34, 64], [38, 22], [42, 80], [46, 44], [52, 72], [58, 34], [64, 90], [70, 50],
    [76, 26], [82, 66], [88, 42], [94, 18], [14, 50], [28, 36], [50, 58], [66, 24],
    [40, 96], [72, 78], [86, 84], [18, 84], [44, 30], [60, 62],
  ];
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <span className="h-3 w-1 rounded-full bg-sky-500" />
        <span className="text-[9px] font-semibold text-slate-700">{title}</span>
      </div>
      <svg viewBox="0 0 200 110" className="mt-2 h-[150px] w-full">
        {[22, 44, 66, 88].map((y) => (
          <line key={y} x1="6" x2="196" y1={y} y2={y} stroke="#e2e8f0" strokeWidth="0.6" />
        ))}
        {pts.map(([x, y], i) => (
          <circle key={i} cx={6 + (x / 100) * 190} cy={6 + ((100 - y) / 100) * 96} r="2.4" fill="#38bdf8" opacity="0.8" />
        ))}
      </svg>
    </div>
  );
}

function BarChart({ title, bars }) {
  const max = Math.max(...bars.map((b) => b.value));
  const MAX_BAR = 118;
  return (
    <div>
      <div className="flex items-center gap-1.5">
        <span className="h-3 w-1 rounded-full bg-sky-500" />
        <span className="text-[9px] font-semibold text-slate-700">{title}</span>
      </div>
      <div className="mt-3 flex items-end gap-4 px-1">
        {bars.map((b) => (
          <div key={b.label} className="flex flex-1 flex-col items-center justify-end">
            <span className="mb-1 text-[8px] font-semibold text-slate-600">{b.display}</span>
            <div
              className="w-full rounded-t-[3px] bg-gradient-to-t from-sky-600 to-cyan-400"
              style={{ height: `${Math.max(6, (b.value / max) * MAX_BAR)}px` }}
            />
            <span className="mt-1.5 text-[8px] text-slate-400">{b.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ── Animated conversational-analytics demo (ported from landing
      premium/ConversationVisual.tsx, non-interactive auto-play mode) ── */

const AC = { dot: 'bg-sky-400', cursor: 'bg-sky-500', send: 'bg-sky-500' };

function ConversationVisual({
  capability,
  workspaceLabel,
  scenes,
  inputPlaceholder = 'Ask me to analyze or visualize data…',
  thinkingLabel = 'Analyzing your data…',
}) {
  const sceneList = scenes || [];
  const [sceneIndex, setSceneIndex] = useState(0);
  const [typed, setTyped] = useState(0);
  const [phase, setPhase] = useState('typing');
  const total = sceneList.length;
  const active = sceneList[sceneIndex] || sceneList[0];

  useEffect(() => {
    let cancelled = false;
    const timers = [];
    const text = (sceneList[sceneIndex] || sceneList[0]).prompt;

    setTyped(0);
    setPhase('typing');
    let i = 0;
    const typeInt = setInterval(() => {
      if (cancelled) return;
      i += 1;
      setTyped(i);
      if (i >= text.length) {
        clearInterval(typeInt);
        timers.push(setTimeout(() => {
          if (cancelled) return;
          setPhase('thinking');
          timers.push(setTimeout(() => {
            if (cancelled) return;
            setPhase('answer');
            if (total > 1) {
              timers.push(setTimeout(() => {
                if (cancelled) return;
                setSceneIndex((p) => (p + 1) % total);
              }, 6500));
            }
          }, 1500));
        }, 550));
      }
    }, 52);

    return () => {
      cancelled = true;
      clearInterval(typeInt);
      timers.forEach((t) => clearTimeout(t));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneIndex, total]);

  const activeSuggestions = active.suggestions || [];

  return (
    <div className="bg-white">
      {/* top bar */}
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
        <span className="text-[8px] font-semibold uppercase tracking-[0.14em] text-slate-400">
          Intelligence Navigator · Agentic AI
        </span>
        <span className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-[8px] font-medium text-slate-400">
          Capability <span className="font-semibold text-slate-700">{capability}</span>
        </span>
      </div>

      <div className="px-4 pb-4 pt-3">
        <p className="text-[8px] font-semibold uppercase tracking-[0.14em] text-slate-400">{workspaceLabel}</p>

        {/* prompt bubble */}
        <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50/70 px-3 py-2.5">
          <p className="text-[8px] font-semibold uppercase tracking-wide text-slate-400">Prompt</p>
          <p className="mt-1 text-[12px] font-medium leading-snug text-slate-700">
            {active.prompt.slice(0, typed)}
            {phase === 'typing' && (
              <span className={`ml-0.5 inline-block h-[12px] w-[1.5px] -translate-y-[1px] animate-pulse align-middle ${AC.cursor}`} />
            )}
          </p>
        </div>

        {/* answer area — fixed min height to prevent shake */}
        <div className="mt-3 min-h-[280px]">
          <AnimatePresence mode="wait">
            {phase === 'thinking' && (
              <motion.div
                key={`thinking-${sceneIndex}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center gap-2 px-1 pt-2"
              >
                <span className="flex items-center gap-1">
                  {[0, 1, 2].map((d) => (
                    <motion.span
                      key={d}
                      className={`h-1.5 w-1.5 rounded-full ${AC.dot}`}
                      animate={{ opacity: [0.3, 1, 0.3] }}
                      transition={{ duration: 1, repeat: Infinity, delay: d * 0.2 }}
                    />
                  ))}
                </span>
                <span className="text-[10px] font-medium text-slate-400">{thinkingLabel}</span>
              </motion.div>
            )}

            {phase === 'answer' && (
              <motion.div
                key={`answer-${sceneIndex}`}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="text-[12.5px] font-bold leading-snug text-slate-900">{active.answerTitle}</p>
                  {active.chart && (
                    <span className="inline-flex flex-none items-center gap-1 rounded-md border border-sky-200 bg-sky-50 px-1.5 py-1 text-[8px] font-semibold text-sky-600">
                      <svg className="h-2.5 w-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M12 5v14M5 12h14" /></svg>
                      Add to Dashboard
                    </span>
                  )}
                </div>
                {active.answerBody && <p className="mt-1 text-[10px] leading-relaxed text-slate-500">{active.answerBody}</p>}

                {active.chart && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.25 }}
                    className="mt-2.5 rounded-lg border border-slate-200 p-2"
                  >
                    {active.chart}
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* suggestions + input */}
        {activeSuggestions.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {activeSuggestions.map((s) => (
              <span key={s} className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-[8px] font-medium text-slate-500">
                {s}
              </span>
            ))}
          </div>
        )}
        <div className="mt-2.5 flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
          <span className="text-[10px] text-slate-400">{inputPlaceholder}</span>
          <span className={`flex h-5 w-5 items-center justify-center rounded-md text-white ${AC.send}`}>
            <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M7 17 17 7M9 7h8v8" /></svg>
          </span>
        </div>

        {/* scene progress dots */}
        {total > 1 && (
          <div className="mt-3 flex items-center justify-center gap-1.5">
            {sceneList.map((_, i) => (
              <span
                key={i}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  i === sceneIndex ? `w-4 ${AC.cursor}` : 'w-1.5 bg-slate-300'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Laptop mockup frame ── */

function Laptop({ children }) {
  return (
    <div className="relative mx-auto w-full select-none">
      <div
        className="pointer-events-none absolute -inset-x-6 -top-8 bottom-2 rounded-[2rem] opacity-70 blur-2xl"
        style={{ background: 'radial-gradient(60% 60% at 50% 30%, rgba(56,189,248,0.30) 0%, transparent 70%)' }}
      />
      {/* screen */}
      <div className="relative rounded-[14px] border border-slate-200 bg-[#0e2a45] p-1.5 shadow-[0_40px_90px_-30px_rgba(15,42,69,0.45)] ring-1 ring-white/10">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-[14px] bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        <div className="flex items-center justify-center pb-1">
          <span className="h-1 w-1 rounded-full bg-white/30" />
        </div>
        <div className="overflow-hidden rounded-md bg-white shadow-inner">{children}</div>
      </div>
      {/* base / hinge */}
      <div className="relative mx-auto h-[9px] w-[114%] -translate-x-[6.1%] rounded-b-2xl bg-gradient-to-b from-[#14365a] to-[#0a1f33] shadow-lg ring-1 ring-white/5">
        <div className="absolute left-1/2 top-0 h-[5px] w-[13%] -translate-x-1/2 rounded-b-lg bg-black/35" />
      </div>
    </div>
  );
}

/* Scenes for the animated analytics demo. */
const BRAND_DEMO_SCENES = [
  {
    prompt: 'How do loyalty points relate to days since last purchase?',
    answerTitle: 'Loyalty Points vs Days Since Last Purchase',
    answerBody:
      'Your most loyal customers buy most recently — points and recency move together strongly.',
    chart: <ScatterChart title="Loyalty Points vs Days Since Last Purchase" />,
    suggestions: ['Break this down by segment', 'Who is most at risk of churn?'],
  },
  {
    prompt: 'Show average customer lifetime value by segment',
    answerTitle: 'Average Customer Lifetime Value by Segment',
    answerBody: 'Premium customers are worth roughly 2.8× the Standard segment.',
    chart: (
      <BarChart
        title="Average Customer Lifetime Value by Segment"
        bars={[
          { label: 'Premium', value: 3525, display: '$3,526' },
          { label: 'Standard', value: 1238, display: '$1,238' },
          { label: 'Budget', value: 305, display: '$306' },
          { label: 'New', value: 168, display: '$168' },
        ]}
      />
    ),
    suggestions: ['What drives Premium value?'],
  },
];

/* ── The brand panel itself ── */

export function AuthProductVisual() {
  return (
    <Laptop>
      <ConversationVisual
        capability="Conversational Analytics"
        workspaceLabel="Intelligence Navigator"
        inputPlaceholder="Ask anything about your business data…"
        scenes={BRAND_DEMO_SCENES}
      />
    </Laptop>
  );
}

export default function AuthBrandPanel() {
  return (
    <aside
      className="relative hidden overflow-hidden px-10 py-10 lg:flex lg:flex-col lg:justify-between xl:px-16 xl:py-12"
      style={{
        background:
          'linear-gradient(110deg, #eef4fb 0%, #ecf2fb 42%, #f2f6fc 70%, #f8fbfe 100%)',
      }}
    >
      {/* Soft lavender/pink wash in the top-left corner */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            'radial-gradient(115% 80% at 0% 0%, rgba(222,196,236,0.55) 0%, rgba(222,196,236,0.18) 32%, transparent 58%)',
        }}
      />

      <a
        href="https://www.xpherium.com"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Xpherium — visit xpherium.com"
        className="relative z-10 inline-flex w-fit items-center rounded-lg transition-opacity hover:opacity-90"
      >
        <XpheriumLogo variant="dark" size="md" />
      </a>

      <div className="relative z-10 grid flex-1 items-center gap-12 py-8 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.92fr)]">
        <div className="max-w-lg">
          <h2 className="text-3xl font-bold leading-[1.12] tracking-tight text-slate-900 xl:text-[2.85rem]">
            Get started with Intelligence Navigator
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed text-slate-600">
            One unified platform for analytics, AI automation and more — built to help you
            turn your data into a strategic advantage.
          </p>

          <p className="mt-6 text-[15px] font-semibold text-slate-800">
            Intelligence Navigator helps you:
          </p>
          <ul className="mt-3 space-y-2.5">
            {[
              'Track the KPIs that matter with live, auto-built dashboards',
              'Turn raw data into business insights, reports and decks automatically',
              'Run forecasting, sentiment analysis and competitor research in one workflow',
              'Ask questions in plain English and deploy AI agents over your own data',
            ].map((t) => (
              <li key={t} className="flex items-start gap-2.5 text-[14px] leading-snug text-slate-700">
                <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-sky-500/15 ring-1 ring-sky-400/40">
                  <Check className="h-3 w-3 text-sky-600" />
                </span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* Animated analytics demo inside a laptop */}
        <div className="relative mx-auto w-full max-w-[450px]">
          <AuthProductVisual />
        </div>
      </div>

      <p className="relative z-10 text-xs text-slate-400">
        Turn your data into decisions — trusted by modern teams · xpherium.com
      </p>
    </aside>
  );
}
